import { startAppListening } from '../../app/listener';
import {
    getGigakombiFromUrl,
    getSalesChannel,
    getSubscriptionId,
} from './helpers/getSubscriptionDataHelper';
import {
    SetInitialStateArgs,
    initApp,
    nextChunk,
    selectCurrentChunk,
    selectDevice,
    selectManufacturer,
    selectSalesChannel,
    selectSelectedFacets,
    selectSubscriptionId,
    setInitialState,
    toggleAccordion,
    toggleFilter,
} from './slice';
import {
    ADDITIONAL_PAGE_OPTIONS,
    FLOW_DEVICES_FIRST,
    FLOW_SUBSCRIPTIONS_FIRST,
    SAILS_BNT_STORAGE,
    SAILS_PARAM_ATOMIC_ID,
    SAILS_PARAM_FLOW_TYPE,
    SAILS_PARAM_SALESCHANNEL,
    SAILS_PARAM_SUB_ID,
    SAILS_REDPLUS_STORAGE,
    SALESCHANNEL_REDPLUS,
    URL_PARAM_GIGAKOMBI,
} from '@vfde-sails/constants';
import { getHardwareOverview } from '../../api/glados';
import {
    SailsBntStorage,
    getSessionStorageItemJson,
    setSessionStorageItemJson,
    updateStorage,
} from '@vfde-sails/storage';
import { SailsDeviceOverviewStorage } from '../../interfaces/storage';
import { getFiltersFromUrl } from './helpers/getFiltersFromUrl';
import {
    createQueryString,
    createUrlWithQueryString,
    decodeHtml,
    getQueryParam,
    updateUrl,
} from '@vfde-sails/utils';
import {
    CATEGORY_QUERYPARAM,
    CHUNK_INIT,
    CHUNK_QUERYPARAM,
} from '../../app/constants';
import {
    MAP_SALESCHANNEL_TO_TRACKING_FILTER,
    TRACKING_INFORMATION_ACTION_REVEAL,
    TrackType,
    TrackingError,
    TrackingErrorType,
    TrackingFilter,
    TrackingFilterType,
    TrackingInformationTrigger,
    TrackingInformationUiType,
    trackIt,
} from '@vfde-sails/tracking';
import {
    createPageViewTrackingData,
    createPageViewTrackingDataWithError,
} from './helpers/createPageViewTrackingData';
import { createTrackingFilterObject } from './helpers/createTrackingFilterObject';
import { getTrackingFlowType } from './helpers/getTrackingFlowType';
import { TrackingData } from '../../interfaces/tracking';

export const listeners = {
    /**
     * Adds the listener for the `initApp` action.
     * It then collects the relevant initialization data and dispatches the `setInitialState` action.
     */
    initApp: () => startAppListening({
        actionCreator: initApp,
        effect: (_action, listenerApi) => {
            const salesChannel = getSalesChannel();
            const subscriptionId = getSubscriptionId(salesChannel);
            const facetLabels = getFiltersFromUrl();
            const currentChunk = Math.max(1, parseInt(getQueryParam(CHUNK_QUERYPARAM)!, 10) || CHUNK_INIT);
            const manufacturer = window[ADDITIONAL_PAGE_OPTIONS]?.hardwareManufacturer || '';

            const setInitialStateArgs: SetInitialStateArgs = {
                salesChannel,
                subscriptionId,
                manufacturer,
                facetLabels,
                currentChunk,
            };

            listenerApi.dispatch(
                setInitialState(setInitialStateArgs),
            );
        },
    }),
    /**
     * Adds the listener for the `setInitialState.fulfilled` case.
     * It then updates the storage with relevant properties and initiates the `getHardwareOverview` action.
     */
    setInitialStateFulfilled: () => startAppListening({
        actionCreator: setInitialState.fulfilled,
        effect: (action, listenerApi) => {
            const { salesChannel } = action.payload;

            const storage = {
                [SAILS_PARAM_SALESCHANNEL]: salesChannel,
            } as SailsDeviceOverviewStorage & SailsBntStorage;

            const gigakombiFromUrl = getGigakombiFromUrl();

            if (gigakombiFromUrl) {
                // TODO: url param constant is misused as storage property
                storage[URL_PARAM_GIGAKOMBI] = true;
            }

            const oldStorage = getSessionStorageItemJson<SailsBntStorage>(SAILS_BNT_STORAGE);

            if (!oldStorage?.[SAILS_PARAM_FLOW_TYPE]) {
                storage[SAILS_PARAM_FLOW_TYPE] = action.payload.subscriptionId ? FLOW_SUBSCRIPTIONS_FIRST : FLOW_DEVICES_FIRST;
            }

            if (action.payload.subscriptionId) {
                storage[SAILS_PARAM_SUB_ID] = {
                    [salesChannel!]: action.payload.subscriptionId,
                };
            }

            // If salesChannel is redplus and there is subscriptionId, we should update redplus_flow storage
            if (salesChannel === SALESCHANNEL_REDPLUS && action.payload.subscriptionId) {
                setSessionStorageItemJson(
                    SAILS_REDPLUS_STORAGE,
                    {
                        [SAILS_PARAM_SUB_ID]: action.payload.subscriptionId,
                    },
                );
            }

            updateStorage(SAILS_BNT_STORAGE, storage, oldStorage, { shouldDeepMerge: true });

            listenerApi.dispatch(getHardwareOverview.initiate());
        },
    }),
    /**
     * Adds the listener for the `setInitialState.rejected` case.
     * It then redirects the user to the URL from payload (if given).
     */
    setInitialStateRejected: () => startAppListening({
        actionCreator: setInitialState.rejected,
        effect: action => {
            action.payload && window.location.replace(action.payload);
        },
    }),
    /**
     * Adds the listener for the `getHardwareOverview.matchFulfilled` case.
     * It then selects relevant data to track the initial pageview event.
     */
    getHardwareOverviewFulfilled: () => startAppListening({
        matcher: getHardwareOverview.matchFulfilled,
        effect: (_action, listenerApi) => {
            // unsubscribe immediately because we only want to track the initial pageview
            listenerApi.unsubscribe();

            const state = listenerApi.getState();

            const salesChannel = selectSalesChannel(state)!;
            const manufacturer = selectManufacturer(state);
            const subscriptionId = selectSubscriptionId(state);
            const selectedFacets = selectSelectedFacets(state);

            const flowType = getTrackingFlowType(salesChannel);

            const trackingData: TrackingData = {
                subscriptionId,
                salesChannel,
                manufacturer,
                selectedFacets,
                flowType,
            };

            trackIt(
                TrackType.PageView,
                createPageViewTrackingData(trackingData),
            );
        },
    }),
    /**
     * Adds the listener for the `getHardwareOverview.matchRejected` case.
     * It then selects relevant data to track the initial error pageview event.
     */
    getHardwareOverviewRejected: () => startAppListening({
        matcher: getHardwareOverview.matchRejected,
        effect: (action, listenerApi) => {
            // unsubscribe immediately because we only want to track the initial pageview
            listenerApi.unsubscribe();

            const state = listenerApi.getState();

            const salesChannel = selectSalesChannel(state)!;
            const manufacturer = selectManufacturer(state);
            const subscriptionId = selectSubscriptionId(state);
            const selectedFacets = selectSelectedFacets(state);

            const flowType = getTrackingFlowType(salesChannel);

            const trackingData: TrackingData = {
                subscriptionId,
                salesChannel,
                manufacturer,
                selectedFacets,
                flowType,
            };

            const trackingError: TrackingError = {
                /* eslint-disable camelcase, @typescript-eslint/naming-convention */
                error_type: TrackingErrorType.Technical,
                error_message: action.payload!.message,
                error_code: action.payload!.status?.toString(),
                error_location: action.meta.arg.endpointName,
                /* eslint-enable camelcase, @typescript-eslint/naming-convention */
            };

            trackIt(
                TrackType.PageView,
                createPageViewTrackingDataWithError(trackingData, trackingError),
            );
        },
    }),
    /**
     * Adds the listener for the `toggleFilter` action.
     * It then updates the URL param and tracks the toggled filter.
     */
    toggleFilter: () => startAppListening({
        actionCreator: toggleFilter,
        effect: (action, listenerApi) => {
            const state = listenerApi.getState();
            const selectedFacets = selectSelectedFacets(state);
            const currentChunk = selectCurrentChunk(state);

            // update URL param with selected filters

            updateUrl(
                createQueryString(
                    {
                        [CATEGORY_QUERYPARAM]: selectedFacets.map(facet => facet.label).toString(),
                        [CHUNK_QUERYPARAM]: currentChunk,
                    },
                    window.location.search,
                ),
                true,
            );

            // track added/removed filters

            const subscriptionId = selectSubscriptionId(state);
            const salesChannel = selectSalesChannel(state);

            let type = TrackingFilterType.Add;

            const filterExists = selectedFacets.some(selectedFacet => selectedFacet.label === action.payload.label);

            if (!filterExists) {
                type = TrackingFilterType.Remove;
            }

            const updatedFilter = [createTrackingFilterObject(action.payload.label)];
            const appliedFilters: TrackingFilter[] = [];
            selectedFacets.forEach(selectedFacet => appliedFilters.push(createTrackingFilterObject(selectedFacet.label)));

            const trackingPayload = {
                action: type,
                updatedFilter,
                appliedFilters,
            };

            if (subscriptionId) {
                trackingPayload.appliedFilters.push(createTrackingFilterObject(MAP_SALESCHANNEL_TO_TRACKING_FILTER[salesChannel!]));
            }

            trackIt(
                TrackType.FilterProducts,
                trackingPayload,
            );
        },
    }),
    /**
     * Adds the listener for the `toggleAccordion` action.
     * It then updates the URL param and tracks the toggled filter.
     */
    toggleAccordion: () => startAppListening({
        actionCreator: toggleAccordion,
        effect: action => {
            if (!action.payload.optOpen) {
                return;
            }

            const trackingPayload = {
                /* eslint-disable camelcase, @typescript-eslint/naming-convention */
                information_action: TRACKING_INFORMATION_ACTION_REVEAL,
                information_ui_type: TrackingInformationUiType.Faq,
                information_trigger: TrackingInformationTrigger.Accordion,
                information_name: decodeHtml(action.payload.stdHeadline),
                /* eslint-enable camelcase, @typescript-eslint/naming-convention */
            };

            trackIt(TrackType.Information, trackingPayload);
        },
    }),
    /**
     * Adds the listener for the `nextChunk` action.
     * It then updates the URL param with the current chunk.
     */
    nextChunk: () => startAppListening({
        actionCreator: nextChunk,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const currentChunk = selectCurrentChunk(state);
            const url = createUrlWithQueryString(CHUNK_QUERYPARAM, currentChunk.toString());

            updateUrl(url, true);
        },
    }),
    /**
     * Adds the listener for the `selectDevice` action.
     * It then updates the storage and redirects to the DDP.
     */
    selectDevice: () => startAppListening({
        actionCreator: selectDevice,
        effect: action => {
            const { productId, productUrl } = action.payload;
            const storage: Partial<SailsBntStorage> = {
                [SAILS_PARAM_ATOMIC_ID]: productId,
            };

            updateStorage(SAILS_BNT_STORAGE, storage, { shouldDeepMerge: true });

            // redirect to the clicked product
            window.location.assign(productUrl);
        },
    }),
};

/**
 * Starts all listeners
 */
export const startListeners = () => {
    const unsubscribeListeners = Object.values(listeners).map(listener => listener());

    return () => {
        unsubscribeListeners.forEach(unsubscribeListener => unsubscribeListener());
    };
};
